import { useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import Chip from "@mui/material/Chip";
import { useFormContext } from "react-hook-form";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  INPUT_COLOR_BORDER,
  BOX_SHADOW_THEME,
  RED_COLOR,
} from "../../../../utils/colors";
import { BORDER_RADIUS } from "../../../../utils/constants";
import { getAllErrorItems } from "../../utils/flattenFormErrors";
import { mapErrorToEntity } from "../../utils/mapErrorToEntity";
import {
  extractFieldNameFromPath,
  getFieldLabel,
} from "../../utils/getFieldNameFromPath";
import { useTranslationApp } from "../../../../lib/i18next";
import { ERRORS, FIELD } from "../../../../i18n/keysTranslations";
import focusFieldByPath from "../../utils/focusFieldByPath";
import { useApprovalCommentFieldHint } from "../CampaignApproval/approvalCommentFieldHintContext";

const FloatingValidationErrorsPanel = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const {
    formState: { errors },
    getValues,
    onChangeSectionSelected,
    onChangeIsForcedSetupPage,
  } = useFormContext();

  const { t } = useTranslationApp();
  const { closeApprovalFieldHint } = useApprovalCommentFieldHint();

  // Flatten errors to get all error items
  const errorItems = getAllErrorItems(errors || {});

  // Map each error to entity information and extract field name
  const errorsWithEntity = errorItems
    .map((errorItem) => {
      const entityInfo = mapErrorToEntity(errorItem.path, getValues);
      const { fieldName, index } = extractFieldNameFromPath(errorItem.path);
      const fieldLabel = fieldName ? getFieldLabel(fieldName, t, index) : null;

      return {
        ...errorItem,
        entityInfo,
        fieldName,
        fieldLabel,
        fieldIndex: index,
      };
    })
    .filter((item) => item.entityInfo !== null);

  const handleErrorClick = useCallback(
    (errorItem) => {
      const { entityInfo, path } = errorItem;

      closeApprovalFieldHint();

      // Handle onboarding errors
      if (entityInfo.entityType === "onboarding") {
        if (onChangeIsForcedSetupPage) {
          onChangeIsForcedSetupPage(true);
        }
        if (onChangeSectionSelected) {
          onChangeSectionSelected(entityInfo.navigationPayload);
        }
        return;
      }

      // Navigate to entity
      if (onChangeSectionSelected) {
        onChangeSectionSelected(entityInfo.navigationPayload);
      }

      // Attempt to focus the field after navigation
      // Wait a bit for the form to mount
      setTimeout(() => {
        focusFieldByPath(path, 2);
      }, 300);
    },
    [closeApprovalFieldHint, onChangeSectionSelected, onChangeIsForcedSetupPage]
  );

  const getEntityLabel = (entityType) => {
    if (entityType === "onboarding") return "Setup";

    const translated = t(entityType);
    if (translated && translated !== entityType) return translated;

    return entityType;
  };

  // Don't render if there are no errors
  if (errorsWithEntity.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 88,
        left: "50%",
        transform: "translateX(-50%)",
        width: 450,
        maxWidth: "calc(100vw - 32px)",
        zIndex: 1200,
        backgroundColor: "background.paper",
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderRadius: BORDER_RADIUS,
        boxShadow: BOX_SHADOW_THEME,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Error List - Show first, then header when expanded */}
      <Collapse in={isExpanded} orientation="vertical">
        <Box
          sx={{
            maxHeight: 400,
            overflowY: "auto",
            p: 1,
            order: 1,
          }}
        >
          {errorsWithEntity.map((errorItem, index) => {
            const { entityInfo, message, path, fieldLabel } = errorItem;
            return (
              <Box
                key={`${path}-${index}`}
                onClick={() => handleErrorClick(errorItem)}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: BORDER_RADIUS,
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  backgroundColor: "fields.background",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    borderColor: RED_COLOR,
                  },
                }}
              >
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Chip
                    label={getEntityLabel(entityInfo.entityType)}
                    size="small"
                    variant="outlined"
                    sx={{
                      ml: -0.25,
                      alignSelf: "flex-start",
                      fontSize: "0.7rem",
                      borderColor: INPUT_COLOR_BORDER,
                      color: "text.primary",
                      backgroundColor: "transparent",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {entityInfo.entityName}
                  </Typography>
                  {fieldLabel && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: "block",
                        fontWeight: 500,
                      }}
                    >
                      {t(FIELD)}: {fieldLabel}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color: "error.main",
                      display: "block",
                      wordBreak: "break-word",
                    }}
                  >
                    {message}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>

      {/* Header - Always visible at bottom */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
          backgroundColor: "fields.background",
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          cursor: "pointer",
          order: 2,
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ErrorOutlineIcon sx={{ color: `${RED_COLOR} !important` }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: RED_COLOR }}
          >
            {t(ERRORS)} ({errorsWithEntity.length})
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default FloatingValidationErrorsPanel;
